import React, { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { api } from "@/lib/api";
import { BookingProvider, useBooking } from "@/context/BookingContext";
import { BookingShell } from "@/components/BookingShell";
import { CategoryStep } from "@/pages/steps/CategoryStep";
import { ServiceStep } from "@/pages/steps/ServiceStep";
import { DateStep } from "@/pages/steps/DateStep";
import { TimeStep } from "@/pages/steps/TimeStep";
import { DetailsStep } from "@/pages/steps/DetailsStep";
import { ReviewStep } from "@/pages/steps/ReviewStep";
import { SuccessStep } from "@/pages/steps/SuccessStep";
import { ConnectionState } from "@/pages/steps/ConnectionState";

function StepSwitcher({ setFooter }) {
    const { step, direction, category, service, date, time } = useBooking();

    // Guard: if the user reloads, prevent showing steps without prerequisites.
    useEffect(() => {
        // nothing — routing is by step state; guards inside each step.
    }, [step, category, service, date, time]);

    const variants = {
        enter: (dir) => ({ x: dir * 32, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (dir) => ({ x: dir * -32, opacity: 0 }),
    };

    return (
        <AnimatePresence mode="wait" custom={direction}>
            <motion.div
                key={step}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`step-${step}`}
            >
                {step === "category" && <CategoryStep />}
                {step === "service" && <ServiceStep setFooter={setFooter} />}
                {step === "date" && <DateStep setFooter={setFooter} />}
                {step === "time" && <TimeStep setFooter={setFooter} />}
                {step === "details" && <DetailsStep setFooter={setFooter} />}
                {step === "review" && <ReviewStep setFooter={setFooter} />}
                {step === "success" && <SuccessStep />}
            </motion.div>
        </AnimatePresence>
    );
}

function BookingApp() {
    const [footer, setFooter] = useState(null);
    return (
        <BookingShell footer={footer}>
            <StepSwitcher setFooter={setFooter} />
        </BookingShell>
    );
}

export function BookingFlow() {
    const [health, setHealth] = useState(null);
    const [error, setError] = useState(null);

    const check = useCallback(() => {
        setError(null);
        api.health().then(setHealth).catch((e) => { setError(e); setHealth(null); });
    }, []);

    useEffect(() => { check(); }, [check]);

    const ready = health && health.configured && health.provider_resolved;

    if (!ready) {
        return (
            <>
                <Toaster theme="dark" position="top-center" />
                <ConnectionState
                    configured={!!health?.configured}
                    providerResolved={!!health?.provider_resolved}
                    baseUrl={health?.base_url}
                    onRetry={check}
                />
                {error && (
                    <div className="fixed bottom-6 inset-x-0 text-center text-xs text-white/40">
                        {error.message}
                    </div>
                )}
            </>
        );
    }

    return (
        <BookingProvider>
            <Toaster theme="dark" position="top-center" />
            <BookingApp />
        </BookingProvider>
    );
}
